import React, { useState, useEffect } from 'react';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography,
  AppBar, Toolbar, IconButton, Avatar, Divider, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, Chip, CircularProgress,
  Alert, Button, TextField, MenuItem, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogActions,
} from '@mui/material';
import {
  Menu as MenuIcon,
  DashboardOutlined as DashboardIcon,
  HistoryOutlined as AuditIcon,
  PeopleOutlined as UsersIcon,
  LogoutOutlined as LogoutIcon,
  SearchOutlined as SearchIcon,
  LockReset as LockResetIcon,
  ReportProblemOutlined as BreachIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import api from '../../services/api';
import ChangePasswordDialog from '../Auth/ChangePassword';

const DRAWER_WIDTH = 240;

const ROLE_LABELS = {
  admin: 'System Admin', radiologist: 'Radiologist', clinician: 'Clinician',
  super_clinician: 'Super Clinician', technologist: 'Technologist', radiographer: 'Radiographer',
  sonographer: 'Sonographer', referring_physician: 'Referring Physician', nurse: 'Nurse',
  medical_records_officer: 'Medical Records Officer', facility_admin: 'Facility Admin',
  it_admin: 'IT Admin', compliance_officer: 'Compliance Officer', compliance_internal: 'Compliance (Internal)',
  tech_support: 'Tech Support', product_lead: 'Product Lead', customer_success: 'Customer Success',
};

function OverviewView({ user }) {
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Compliance Officer</Typography>
        <Typography variant="body2" color="text.secondary">
          Logged in as {user?.firstName} {user?.lastName} &middot; {user?.email}
        </Typography>
        {user?.facility?.facilityName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Facility: {user.facility.facilityName}
          </Typography>
        )}
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Access Scope</Typography>
        <Typography variant="body2" color="text.secondary">
          As a Compliance Officer you have read-only access to user account data and audit information within your facility.
          You cannot create, modify, or delete any records. For system-level compliance queries, contact the system administrator.
        </Typography>
      </Paper>
    </Box>
  );
}

function UsersView({ facilityId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: 500 });
        if (facilityId) params.append('facility', facilityId);
        const res = await api.get(`/users?${params}`);
        setUsers(res.data.users || res.data || []);
      } catch {
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [facilityId]);

  const filtered = users.filter(u => {
    const matchesSearch = !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchesRole = !roleFilter || u.role === roleFilter;
    const matchesStatus = !statusFilter || (statusFilter === 'active' ? u.isActive : !u.isActive);
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 240 }}
        />
        <TextField select size="small" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} sx={{ minWidth: 180 }} label="Filter by role">
          <MenuItem value="">All Roles</MenuItem>
          {Object.entries(ROLE_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
        </TextField>
        <TextField select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }} label="Status">
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
        </TextField>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(u => (
              <TableRow key={u._id} hover>
                <TableCell>{u.firstName} {u.lastName}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{u.email}</TableCell>
                <TableCell>{ROLE_LABELS[u.role] || u.role}</TableCell>
                <TableCell>
                  <Chip label={u.isActive ? 'Active' : 'Suspended'} size="small" color={u.isActive ? 'success' : 'default'} />
                </TableCell>
                <TableCell>
                  <Chip label={u.approvalStatus || 'approved'} size="small"
                    color={u.approvalStatus === 'approved' ? 'success' : u.approvalStatus === 'pending' ? 'warning' : 'error'}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {new Date(u.createdAt).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
        Showing {filtered.length} of {users.length} accounts (read-only)
      </Typography>
    </Box>
  );
}

function AuditView() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error] = useState('');

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = await api.get('/audit-logs?limit=200');
        setLogs(res.data.logs || res.data || []);
      } catch {
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  if (error) return <Alert severity="error">{error}</Alert>;

  if (logs.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <AuditIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">No audit logs available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Audit log recording will appear here once configured.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Timestamp</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Resource</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((log, i) => (
            <TableRow key={log._id || i} hover>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                {new Date(log.createdAt || log.timestamp).toLocaleString()}
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{log.user?.email || log.userId || '—'}</TableCell>
              <TableCell>{log.action || log.event || '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{log.resource || log.target || '—'}</TableCell>
              <TableCell>
                <Chip label={log.result || log.status || 'ok'} size="small"
                  color={log.result === 'error' || log.status === 'error' ? 'error' : 'success'}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

// NDPA 2023 #103 — Breach Register (s.40). Includes the 72-hour NDPC clock.
const BREACH_STATUS_META = {
  detected: { label: 'Detected', color: 'error' },
  investigating: { label: 'Investigating', color: 'warning' },
  contained: { label: 'Contained', color: 'info' },
  ndpc_notified: { label: 'NDPC Notified', color: 'secondary' },
  subjects_notified: { label: 'Subjects Notified', color: 'secondary' },
  remediated: { label: 'Remediated', color: 'success' },
  closed: { label: 'Closed', color: 'default' },
};

const SEVERITY_COLOR = { low: 'default', medium: 'warning', high: 'error', critical: 'error' };

function hoursToDeadline(detectedAt) {
  if (!detectedAt) return null;
  const deadline = new Date(detectedAt).getTime() + 72 * 60 * 60 * 1000;
  return Math.round((deadline - Date.now()) / (60 * 60 * 1000));
}

function BreachRegisterView() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', severity: 'medium', breachType: 'other',
    affectedCount: '', notifiableToNdp: true, isHighRisk: false, occurredAt: ''
  });

  const fetchIncidents = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (statusFilter) params.append('status', statusFilter);
      const res = await api.get(`/compliance/breach?${params}`);
      setIncidents(res.data.incidents || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load breach register');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIncidents(); /* eslint-disable-next-line */ }, [statusFilter]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.description.trim()) return;
    try {
      setCreating(true);
      await api.post('/compliance/breach', {
        ...form,
        affectedCount: form.affectedCount ? Number(form.affectedCount) : 0,
        occurredAt: form.occurredAt || undefined,
      });
      setDialogOpen(false);
      setForm({ title: '', description: '', severity: 'medium', breachType: 'other', affectedCount: '', notifiableToNdp: true, isHighRisk: false, occurredAt: '' });
      fetchIncidents();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create incident');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Status" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Statuses</MenuItem>
          {Object.entries(BREACH_STATUS_META).map(([v, m]) => <MenuItem key={v} value={v}>{m.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchIncidents}>Refresh</Button>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}>
          Register Incident
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : incidents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BreachIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No incidents recorded</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All breaches (including those below the NDPC notification threshold) must be logged here.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Detected</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>72h Clock</TableCell>
                <TableCell>Affected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {incidents.map((it) => {
                const meta = BREACH_STATUS_META[it.status] || { label: it.status, color: 'default' };
                const hrs = hoursToDeadline(it.detectedAt);
                const notified = Boolean(it.ndpcNotifiedAt);
                return (
                  <TableRow key={it._id} hover>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{it.incidentRef}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{it.title}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {it.detectedAt ? new Date(it.detectedAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell><Chip size="small" label={it.severity} color={SEVERITY_COLOR[it.severity] || 'default'} /></TableCell>
                    <TableCell><Chip size="small" label={meta.label} color={meta.color} /></TableCell>
                    <TableCell>
                      {notified ? (
                        <Chip size="small" color="success" label="NDPC notified" />
                      ) : hrs === null ? '—' : hrs < 0 ? (
                        <Chip size="small" color="error" label={`Overdue ${Math.abs(hrs)}h`} />
                      ) : (
                        <Chip size="small" color={hrs <= 24 ? 'warning' : 'default'} label={`${hrs}h left`} />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{it.affectedCount ?? 0}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Register Breach Incident</DialogTitle>
        <Box sx={{ px: 3, pb: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Title" size="small" fullWidth value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <TextField label="Description" size="small" fullWidth multiline minRows={3} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select label="Severity" size="small" fullWidth value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })}>
              {['low', 'medium', 'high', 'critical'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select label="Type" size="small" fullWidth value={form.breachType}
              onChange={(e) => setForm({ ...form, breachType: e.target.value })}>
              {['unauthorised_access', 'disclosure', 'loss', 'alteration', 'destruction', 'ransomware', 'other']
                .map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Affected records" size="small" type="number" fullWidth value={form.affectedCount}
              onChange={(e) => setForm({ ...form, affectedCount: e.target.value })} />
            <TextField label="Occurred at" size="small" type="datetime-local" fullWidth value={form.occurredAt}
              onChange={(e) => setForm({ ...form, occurredAt: e.target.value })}
              InputLabelProps={{ shrink: true }} />
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !form.title.trim() || !form.description.trim()}
            variant="contained" sx={{ textTransform: 'none', bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}>
            {creating ? 'Saving…' : 'Save Incident'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const NAV_ITEMS = [
  { key: 'overview', label: 'Overview', icon: <DashboardIcon /> },
  { key: 'users', label: 'User Accounts', icon: <UsersIcon /> },
  { key: 'audit', label: 'Audit Logs', icon: <AuditIcon /> },
  { key: 'breach', label: 'Breach Register', icon: <BreachIcon /> },
];

export default function ComplianceDashboard() {
  const { user, logout } = useAuth();
  const { mode } = useThemeMode();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const theme = createTheme({
    palette: {
      mode,
      background: { default: mode === 'dark' ? '#0d1117' : '#f8fafc', paper: mode === 'dark' ? '#161b22' : '#ffffff' },
      primary: { main: '#6a1b9a' },
    },
  });

  const facilityId = user?.facility?._id || user?.facility;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#1a0533' }}>
      <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', letterSpacing: 2, textTransform: 'uppercase', mb: 0.5 }}>
          DycoVue
        </Typography>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
          Compliance
        </Typography>
        {user?.facility?.facilityName && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
            {user.facility.facilityName}
          </Typography>
        )}
      </Box>
      <List sx={{ flex: 1, pt: 1, px: 1 }}>
        {NAV_ITEMS.map(item => (
          <ListItem
            key={item.key}
            button
            onClick={() => { setActiveSection(item.key); setMobileOpen(false); }}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              color: activeSection === item.key ? '#fff' : 'rgba(255,255,255,0.55)',
              bgcolor: activeSection === item.key ? 'rgba(255,255,255,0.1)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: activeSection === item.key ? 600 : 400 }} />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: '#6a1b9a', fontSize: '0.85rem' }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }} noWrap>
              {user?.role === 'compliance_internal' ? 'Compliance (Internal)' : 'Compliance Officer'}
            </Typography>
          </Box>
        </Box>
        <Button
          fullWidth size="small" startIcon={<LockResetIcon />}
          onClick={() => setChangePasswordOpen(true)}
          sx={{ color: 'rgba(255,255,255,0.55)', justifyContent: 'flex-start', mb: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#fff' } }}
        >
          Change Password
        </Button>
        <Button
          fullWidth size="small" startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{ color: 'rgba(255,255,255,0.55)', justifyContent: 'flex-start', '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#fff' } }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'users': return <UsersView facilityId={facilityId} />;
      case 'audit': return <AuditView />;
      case 'breach': return <BreachRegisterView />;
      default: return <OverviewView user={user} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        {isMobile ? (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}>
            {drawerContent}
          </Drawer>
        ) : (
          <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' } }}>
            {drawerContent}
          </Drawer>
        )}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {isMobile && (
            <AppBar position="static" elevation={0} sx={{ bgcolor: '#1a0533' }}>
              <Toolbar>
                <IconButton edge="start" color="inherit" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }}>
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Compliance</Typography>
              </Toolbar>
            </AppBar>
          )}
          <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, overflow: 'auto' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {NAV_ITEMS.find(n => n.key === activeSection)?.label || 'Overview'}
            </Typography>
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
